import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ShoppingCart, Wifi, Smartphone, Package, Check, Info } from 'lucide-react'
import { productService } from '@/services/products'
import { useCartStore } from '@/stores/cartStore'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { LoadingScreen } from '@/components/ui/Spinner'
import { Alert } from '@/components/ui/Alert'
import SEOHead from '@/components/SEOHead'
import { usePortal } from '@/contexts/PortalContext'

export const ProductDetail: React.FC = () => {
  // productKey is either a slug (/product/<slug>) or a legacy numeric id
  // (/product/162 or /product/162/<old-name>). Both resolve server-side.
  const { productKey } = useParams<{ productKey: string }>()
  const navigate = useNavigate()
  // This page now also serves white-label portals, where every "Silux Mobile" below would
  // be the wrong brand on someone else's domain — the tab title alone would read
  // "…| Silux Mobile" on a reseller's product page. Off a portal nothing changes.
  const { portal } = usePortal()
  const [addedToCart, setAddedToCart] = useState(false)
  const { addItem, getItemCount } = useCartStore()

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', productKey],
    queryFn: () => productService.getProduct(productKey!),
    enabled: !!productKey,
  })

  // Redirect legacy numeric / old-format URLs to the canonical /product/<slug>.
  React.useEffect(() => {
    if (product?.slug && window.location.pathname !== `/product/${product.slug}`) {
      navigate(`/product/${product.slug}`, { replace: true })
    }
  }, [product?.slug, navigate])

  const getProductIcon = (type: string) => {
    switch (type) {
      case 'sim_only':
        return <Smartphone className="w-12 h-12 text-primary-600" />
      case 'broadband':
        return <Wifi className="w-12 h-12 text-primary-600" />
      case 'bundle':
        return <Package className="w-12 h-12 text-primary-600" />
      default:
        return <Smartphone className="w-12 h-12 text-primary-600" />
    }
  }

  const handleAddToCart = () => {
    if (product) {
      addItem(product)
      setAddedToCart(true)
      setTimeout(() => setAddedToCart(false), 3000)
    }
  }

  if (isLoading) {
    return <LoadingScreen message="Loading product details..." />
  }

  if (error || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Alert variant="error" message="Product not found or failed to load." />
        <Button variant="outline" onClick={() => navigate('/shop')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Shop
        </Button>
      </div>
    )
  }

  // ── SEO ──────────────────────────────────────────────────────────────
  const brandName = portal ? (portal.trading_name || portal.company_name || 'Mobile') : 'Silux Mobile'
  // A portal's canonical must be its own domain, never ours. Portals are served with a
  // noindex header anyway, so this affects what a visitor sees, not what Google indexes —
  // and the main site's canonical is untouched.
  const SITE = portal ? window.location.origin : 'https://www.siluxmobile.co.uk'
  const slug = product.slug || String(product.id)
  const canonical = `${SITE}/product/${slug}`
  const effectivePrice =
    product.discounted_price && product.discounted_price < product.price
      ? product.discounted_price
      : product.price
  const seoDescription =
    product.meta_description ||
    product.short_description ||
    (product.description ? product.description.replace(/<[^>]*>/g, '').slice(0, 160) : '') ||
    `${product.name} from ${brandName} — reliable UK coverage, no hidden fees, fast delivery.`
  const seoKeywords = product.meta_keywords
    ? product.meta_keywords.split(',').map((k) => k.trim()).filter(Boolean)
    : undefined
  const seoImage = product.image_url
    ? product.image_url.startsWith('http')
      ? product.image_url
      : `${SITE}${product.image_url}`
    : undefined
  const productSchema = {
    name: product.name,
    description: seoDescription,
    image: seoImage ? [seoImage] : [],
    sku: product.sku || product.product_code || String(product.id),
    price: effectivePrice,
    priceCurrency: 'GBP',
    availability: (product.is_active ? 'InStock' : 'OutOfStock') as 'InStock' | 'OutOfStock',
    brand: brandName,
    category: product.product_type,
  }
  const breadcrumbs = [
    { name: 'Home', url: `${SITE}/` },
    { name: 'Shop', url: `${SITE}/shop` },
    { name: product.name, url: canonical },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
      <SEOHead
        title={portal ? `${product.name} | ${brandName}` : (product.meta_title || `${product.name} | Silux Mobile`)}
        description={seoDescription}
        keywords={seoKeywords}
        ogImage={seoImage}
        canonical={canonical}
        productSchema={productSchema}
        breadcrumbs={breadcrumbs}
      />
      {/* Back button and cart */}
      <div className="flex justify-between items-center mb-6">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button variant="outline" onClick={() => navigate('/cart')}>
          <ShoppingCart className="w-5 h-5 mr-2" />
          Cart ({getItemCount()})
        </Button>
      </div>

      {addedToCart && (
        <Alert
          variant="success"
          message={`${product.name} added to cart`}
          onClose={() => setAddedToCart(false)}
          className="mb-6"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Product Image */}
        <div>
          <Card className="p-4 sm:p-6">
            <div className="relative">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-auto max-h-96 object-contain rounded-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    const fallback = e.currentTarget.nextElementSibling
                    if (fallback) fallback.classList.remove('hidden')
                  }}
                />
              ) : null}
              <div className={`${product.image_url ? 'hidden' : ''} flex items-center justify-center h-64 bg-gray-100 rounded-lg`}>
                {getProductIcon(product.product_type)}
              </div>
            </div>
          </Card>
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
            {product.short_description && (
              <p className="text-lg text-gray-600">{product.short_description}</p>
            )}
          </div>

          {/* Price Section */}
          <Card className="p-4 sm:p-6">
            <div className="flex items-baseline gap-3 mb-4">
              {product.discounted_price && product.discounted_price < product.price ? (
                <>
                  <span className="text-3xl font-bold text-primary-600">
                    £{product.discounted_price.toFixed(2)}
                  </span>
                  <span className="text-xl text-gray-400 line-through">
                    £{product.price.toFixed(2)}
                  </span>
                  {product.is_contract && <span className="text-gray-500">/month</span>}
                </>
              ) : (
                <>
                  <span className="text-3xl font-bold text-primary-600">
                    £{product.price.toFixed(2)}
                  </span>
                  {product.is_contract && <span className="text-gray-500">/month</span>}
                </>
              )}
            </div>

            {product.admin_fee_enabled && product.admin_fee && (
              <p className="text-sm text-gray-500 mb-4">
                + £{product.admin_fee.toFixed(2)} one-time {product.product_type === 'broadband' ? 'Refundable Holding Deposit - Refunded at the end of the Contract' : 'admin fee'}
              </p>
            )}

            {product.setup_fee && product.setup_fee > 0 && (
              <p className="text-sm text-gray-500 mb-4">
                + £{product.setup_fee.toFixed(2)} setup fee
              </p>
            )}

            <Button fullWidth size="lg" onClick={handleAddToCart}>
              <ShoppingCart className="w-5 h-5 mr-2" />
              Add to Cart
            </Button>
          </Card>

          {/* Specifications */}
          {Boolean(
            product.network_type ||
            (product.is_contract && product.contract_months) ||
            product.data_allowance_gb ||
            product.minutes ||
            product.texts ||
            product.is_pay_and_go
          ) && (
          <Card className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Info className="w-5 h-5 mr-2" />
              Specifications
            </h2>
            <div className="space-y-3">
              {product.network_type && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Network</span>
                  <span className="font-medium capitalize">{product.network_type.replace('_', ' ')}</span>
                </div>
              )}

              {product.is_contract && product.contract_months && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Contract Length</span>
                  <span className="font-medium">{product.contract_months === 1 ? "Rolling monthly (30 days' notice)" : `${product.contract_months} months`}</span>
                </div>
              )}

              {product.data_allowance_gb && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Data Allowance</span>
                  <span className="font-medium">
                    {product.data_allowance_gb === -1 ? 'Unlimited' : `${product.data_allowance_gb} ${(product as any).data_unit || 'GB'}`}
                  </span>
                </div>
              )}

              {product.minutes && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Minutes</span>
                  <span className="font-medium">{product.minutes}</span>
                </div>
              )}

              {product.texts && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Texts</span>
                  <span className="font-medium">{product.texts}</span>
                </div>
              )}

              {product.is_pay_and_go && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Plan Type</span>
                  <Badge variant="success">Pay As You Go</Badge>
                </div>
              )}

            </div>
          </Card>
          )}

          {/* Features */}
          {(product.features && product.features.length > 0) || product.requires_sim || product.requires_router || product.is_pay_and_go ? (
            <Card className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Features</h2>
              <ul className="space-y-2">
                {/* Custom features from admin */}
                {product.features && product.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}

                {/* Auto-generated features (only show if no custom features) */}
                {(!product.features || product.features.length === 0) && (
                  <>
                    {product.requires_sim && (
                      <li className="flex items-center text-gray-700">
                        <Check className="w-5 h-5 text-green-500 mr-2" />
                        SIM card included
                      </li>
                    )}
                    {product.requires_router && (
                      <li className="flex items-center text-gray-700">
                        <Check className="w-5 h-5 text-green-500 mr-2" />
                        Router included
                      </li>
                    )}
                    {product.is_pay_and_go && (
                      <li className="flex items-center text-gray-700">
                        <Check className="w-5 h-5 text-green-500 mr-2" />
                        Flexible pay as you go
                      </li>
                    )}
                  </>
                )}
              </ul>
            </Card>
          ) : null}

          {/* Description */}
          {product.description && (
            <Card className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-600 text-sm whitespace-pre-line leading-relaxed">
                {product.description}
              </p>
            </Card>
          )}

          {/* Roaming Countries Note */}
          {product.network_type?.startsWith('roaming_') && (() => {
            const zoneMap: Record<string, string> = {
              roaming_multinet: 'multinet',
              roaming_zone1: 'zone1',
              roaming_zone2: 'zone2',
              roaming_zone3: 'zone3',
              roaming_zone4: 'zone4',
              roaming_zone5: 'zone5',
              roaming_zone6: 'zone6',
            }
            const zoneKey = zoneMap[product.network_type || ''] || ''
            return (
              <Card className="p-4 sm:p-6 bg-gradient-to-br from-primary-50 to-cyan-50 border-primary-200">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary-100 rounded-lg shrink-0">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary-900 mb-1">International Roaming Coverage</h3>
                    <p className="text-sm text-primary-700 mb-3">
                      Some Zones offer the same countries but on different networks in their respective countries.
                    </p>
                    <a
                      href={`/roaming${zoneKey ? '#' + zoneKey : ''}`}
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-800 underline underline-offset-2"
                    >
                      Review the List of Countries this Sim Covers
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>
              </Card>
            )
          })()}
        </div>
      </div>
    </div>
  )
}

export default ProductDetail
